/**
 * CanvasAnnotator - Post-render DOM annotation for WYSIWYG
 * 
 * After the canvas renders, scans for known editable zones within blocks
 * and adds data-tc-element-* attributes for the WYSIWYG overlay to detect.
 * 
 * Uses MutationObserver to re-annotate on DOM changes.
 * Maps block props to DOM elements via selectors.
 */

import { useEffect, useCallback, useRef } from 'react';

// Map block types to their editable sub-elements
// Each entry: { propKey, selector (within block), type, label }
const BLOCK_ELEMENT_MAP = {
  'hero_overlay': [
    { propKey: 'heading', selector: 'h1, h2', type: 'text', label: 'Heading' },
    { propKey: 'tagline', selector: 'p', type: 'text', label: 'Tagline' },
    { propKey: 'primaryCtaLabel', selector: 'button, a[role="button"], .btn, a.bg-orange-500, a.bg-orange-600', type: 'button', label: 'CTA Button' },
    { propKey: 'backgroundImage', selector: 'img', type: 'image', label: 'Hero Image' },
  ],
  'homepage_cta_banner': [
    { propKey: 'title', selector: 'h2, h3', type: 'text', label: 'Title' },
    { propKey: 'subtitle', selector: 'p', type: 'text', label: 'Subtitle' },
    { propKey: 'ctaLabel', selector: 'button, a', type: 'button', label: 'CTA Button' },
  ],
  'trending_rail': [
    { propKey: 'heading', selector: 'h2, h3', type: 'text', label: 'Heading' },
    { propKey: 'subheading', selector: 'p.text-zinc-400, p.text-zinc-500', type: 'text', label: 'Subheading' },
  ],
  'category_showcase': [
    { propKey: 'heading', selector: 'h2, h3', type: 'text', label: 'Heading' },
    { propKey: 'subheading', selector: 'p', type: 'text', label: 'Subheading' },
  ],
  'creator_spotlight': [
    { propKey: 'heading', selector: 'h2, h3', type: 'text', label: 'Heading' },
    { propKey: 'subheading', selector: 'p', type: 'text', label: 'Subheading' },
  ],
  'art_gallery_grid': [
    { propKey: 'heading', selector: 'h2, h3', type: 'text', label: 'Heading' },
  ],
  'social_proof_strip': [
    { propKey: 'heading', selector: 'h2, h3', type: 'text', label: 'Heading' },
  ],
  'why_join': [
    { propKey: 'heading', selector: 'h2, h3', type: 'text', label: 'Heading' },
  ],
};

/**
 * Annotate a single block's DOM with data-tc-element-* attributes
 */
const annotateBlock = (blockElement, blockId, blockType) => {
  const map = BLOCK_ELEMENT_MAP[blockType];
  if (!map) {
    // Also try without prefix for landing blocks
    const altType = blockType.replace('landing_', '').replace('homepage_', '');
    const altMap = BLOCK_ELEMENT_MAP[altType];
    if (!altMap) return;
    return annotateBlock(blockElement, blockId, altType);
  }
  
  // Remove pointer-events-none from any child containers
  blockElement.querySelectorAll('.pointer-events-none').forEach(el => {
    el.style.pointerEvents = 'auto';
  });
  
  let annotatedCount = 0;
  
  map.forEach(({ propKey, selector, type, label }) => {
    // Try multiple selector variations
    const selectors = selector.split(', ');
    let el = null;
    
    for (const sel of selectors) {
      el = blockElement.querySelector(sel);
      if (el) break;
    }
    
    if (el) {
      const elementId = `${blockId}::${propKey}`;
      el.setAttribute('data-tc-element-id', elementId);
      el.setAttribute('data-tc-element-type', type);
      el.setAttribute('data-tc-element-label', label);
      el.setAttribute('data-tc-block-id', blockId);
      el.setAttribute('data-tc-prop-key', propKey);
      
      // Make element interactive for WYSIWYG
      el.style.cursor = 'pointer';
      el.style.position = el.style.position || 'relative';
      el.style.pointerEvents = 'auto';
      annotatedCount++;
    }
  });
  
  if (annotatedCount > 0) {
    console.debug(`[CanvasAnnotator] Annotated ${annotatedCount} elements in block ${blockId} (${blockType})`);
  }
};

/**
 * useCanvasAnnotator - Hook that annotates canvas DOM after each render
 */
const useCanvasAnnotator = (canvasRef, blocks) => {
  const observerRef = useRef(null);
  const lastAnnotationRef = useRef(0);
  
  const annotateAll = useCallback(() => {
    if (!canvasRef?.current) {
      console.debug('[CanvasAnnotator] No canvasRef available');
      return;
    }
    
    const canvas = canvasRef.current;
    const blockElements = canvas.querySelectorAll('[data-block-id]');
    
    console.debug(`[CanvasAnnotator] Found ${blockElements.length} block elements to annotate`);
    
    if (blockElements.length === 0) {
      // Also check for data-testid patterns that might indicate blocks
      const altBlocks = canvas.querySelectorAll('[data-testid^="block-wrapper-"]');
      console.debug(`[CanvasAnnotator] Alt search found ${altBlocks.length} blocks via data-testid`);
    }
    
    blockElements.forEach((blockEl) => {
      const blockId = blockEl.getAttribute('data-block-id');
      // Find corresponding block data
      const block = blocks?.find(b => b.id === blockId);
      if (block) {
        annotateBlock(blockEl, blockId, block.type);
      }
    });
    
    // Report annotation status
    const annotatedElements = canvas.querySelectorAll('[data-tc-element-id]');
    console.debug(`[CanvasAnnotator] Total annotated elements: ${annotatedElements.length}`);
    lastAnnotationRef.current = Date.now();
  }, [canvasRef, blocks]);
  
  useEffect(() => {
    // Initial annotation (with delay to allow render)
    const timer = setTimeout(annotateAll, 300);
    
    // Watch for DOM mutations and re-annotate
    if (canvasRef?.current) {
      observerRef.current = new MutationObserver(() => {
        // Debounce re-annotation
        clearTimeout(timer);
        setTimeout(annotateAll, 100);
      });
      
      observerRef.current.observe(canvasRef.current, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    }
    
    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [canvasRef, blocks, annotateAll]);
  
  // Re-annotate when blocks change
  useEffect(() => {
    const timer = setTimeout(annotateAll, 200);
    return () => clearTimeout(timer);
  }, [blocks, annotateAll]);
  
  return { annotateAll };
};

export default useCanvasAnnotator;
export { BLOCK_ELEMENT_MAP, annotateBlock };
